import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faTriangleExclamation } from '@/fontawesome';

/**
 * Each dashboard card loads its own data. The dashboard used to await ~26 requests in a
 * single Promise.all and render nothing until the slowest one returned, so one slow endpoint
 * blocked the whole page — and one failing endpoint emptied it.
 */

export type AsyncValue<T> =
  | { status: 'loading' }
  | { status: 'ready'; value: T }
  | { status: 'error' };

export function useAsyncValue<T>(loader: () => Promise<T>): AsyncValue<T> {
  const [state, setState] = useState<AsyncValue<T>>({ status: 'loading' });

  // Kept in a ref so an inline arrow function does not re-trigger the effect on every render.
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    let cancelled = false;

    loaderRef.current()
      .then(value => {
        if (!cancelled) setState({ status: 'ready', value });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Deliberately not toasted: a dozen failing cards would bury the screen in toasts.
        console.error('dashboard card failed to load', err);
        setState({ status: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

// Explicit map — Tailwind only keeps classes it can find as literals in the source.
const ACCENTS = {
  blue: 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400',
  green: 'bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400',
  orange: 'bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400',
  cyan: 'bg-cyan-100 dark:bg-cyan-900/50 text-cyan-600 dark:text-cyan-400',
  purple: 'bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400',
  yellow: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-400',
  indigo: 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400',
  pink: 'bg-pink-100 dark:bg-pink-900/50 text-pink-600 dark:text-pink-400',
  teal: 'bg-teal-100 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400',
  amber: 'bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400',
  emerald: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400',
  rose: 'bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-400',
  red: 'bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400',
  sky: 'bg-sky-100 dark:bg-sky-900/50 text-sky-600 dark:text-sky-400',
} as const;

export type StatCardAccent = keyof typeof ACCENTS;

interface StatCardProps {
  to: string;
  label: string;
  icon: IconDefinition;
  accent: StatCardAccent;
  children: ReactNode;
}

/** Shell of a dashboard card: title, icon tile and a body that the caller fills. */
export function StatCard({ to, label, icon, accent, children }: StatCardProps) {
  return (
    <Link
      to={to}
      className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow cursor-pointer"
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{label}</p>
          {children}
        </div>
        <div className={`w-14 h-14 rounded-lg flex items-center justify-center shrink-0 ${ACCENTS[accent]}`}>
          <FontAwesomeIcon icon={icon} className="w-7 h-7" />
        </div>
      </div>
    </Link>
  );
}

/** Placeholder shown while a card's own request is still in flight. */
export function StatSkeleton({ className = 'h-9 w-20' }: { className?: string }) {
  return <div className={`${className} rounded bg-gray-200 dark:bg-gray-700 animate-pulse`} />;
}

export function StatError() {
  return (
    <p className="text-sm text-red-600 dark:text-red-400 flex items-center" title="Daten konnten nicht geladen werden">
      <FontAwesomeIcon icon={faTriangleExclamation} className="w-4 h-4 mr-1.5" />
      Fehler
    </p>
  );
}

/**
 * Renders a card body from its own async state: skeleton while loading, error marker on
 * failure, the real content once it arrives.
 */
export function StatValue<T>({
  state,
  children,
  skeletonClassName,
}: {
  state: AsyncValue<T>;
  children: (value: T) => ReactNode;
  skeletonClassName?: string;
}) {
  if (state.status === 'loading') return <StatSkeleton className={skeletonClassName} />;
  if (state.status === 'error') return <StatError />;
  return <>{children(state.value)}</>;
}

/** The common case: a single number in the card's big type. */
export function StatNumber({ state }: { state: AsyncValue<number> }) {
  return (
    <StatValue state={state}>
      {value => <p className="text-3xl font-bold text-gray-900 dark:text-white">{value}</p>}
    </StatValue>
  );
}
